import { useState, useEffect } from 'react';
import { Users, UserPlus, MoreHorizontal, Search, Filter, UserX, UserCheck, History, AlertTriangle } from 'lucide-react';
import apiClient from '../../api/client';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';

// Helper function to format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Never';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString();
};

// Status badge component
const StatusBadge = ({ status }) => {
  const variants = {
    active: 'bg-green-100 text-green-800 border-green-200',
    inactive: 'bg-red-100 text-red-800 border-red-200',
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200'
  };
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${variants[status] || variants.active}`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
        status === 'active' ? 'bg-green-500' : 
        status === 'inactive' ? 'bg-red-500' : 'bg-yellow-500'
      }`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Summary cards component
const MemberStatsCards = ({ stats }) => {
  const cards = [
    { label: 'Total Members', value: stats.total, color: 'bg-blue-500' },
    { label: 'Active', value: stats.active, color: 'bg-green-500' },
    { label: 'Inactive', value: stats.inactive, color: 'bg-red-500' },
    { label: 'Pending Invites', value: stats.pending, color: 'bg-yellow-500' }
  ];
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {cards.map((card) => (
        <Card key={card.label} className="border shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-8 rounded-full ${card.color}`} />
              <div>
                <p className="text-2xl font-bold">{card.value}</p>
                <p className="text-sm text-gray-500">{card.label}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Deactivate confirmation dialog
const DeactivateDialog = ({ open, onClose, member, onConfirm, loading }) => {
  const [reason, setReason] = useState('');
  
  const handleConfirm = () => {
    onConfirm(reason);
    setReason('');
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Deactivate Member
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to deactivate <strong>{member?.user?.first_name} {member?.user?.last_name}</strong>?
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-700">
              <strong>This will:</strong>
            </p>
            <ul className="text-sm text-red-600 mt-1 ml-4 list-disc">
              <li>Block the user from logging in</li>
              <li>Revoke all access to the application</li>
              <li>Preserve their data and history</li>
            </ul>
          </div>
          
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason (optional)
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Left company, Role change, Security concern..."
            className="resize-none"
            rows={2}
          />
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirm}
            disabled={loading}
            data-testid="confirm-deactivate-btn"
          >
            {loading ? 'Deactivating...' : 'Deactivate Member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Reactivate confirmation dialog
const ReactivateDialog = ({ open, onClose, member, onConfirm, loading }) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-600">
            <UserCheck className="w-5 h-5" />
            Reactivate Member
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to reactivate <strong>{member?.user?.first_name} {member?.user?.last_name}</strong>?
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-700">
              <strong>This will:</strong>
            </p>
            <ul className="text-sm text-green-600 mt-1 ml-4 list-disc">
              <li>Allow the user to log in again</li>
              <li>Restore their previous role and access</li>
            </ul>
          </div>
          
          {member?.deactivation_reason && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Previous deactivation reason:</p>
              <p className="text-sm text-gray-700">{member.deactivation_reason}</p>
            </div>
          )}
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            className="bg-green-600 hover:bg-green-700"
            onClick={onConfirm}
            disabled={loading}
            data-testid="confirm-reactivate-btn"
          >
            {loading ? 'Reactivating...' : 'Reactivate Member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Activity log drawer
const ActivityLogDialog = ({ open, onClose, member, workspace }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (open && member && workspace) {
      loadAuditLog();
    }
  }, [open, member]);
  
  const loadAuditLog = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(
        `/api/v1/workspaces/${workspace.workspace.id}/members/${member.id}/audit-log`
      );
      if (response.data.success) {
        setLogs(response.data.data.logs || []);
      }
    } catch (error) {
      console.error('Error loading audit log:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const formatEventType = (type) => {
    const labels = {
      'member_deactivated': 'Account Deactivated',
      'member_reactivated': 'Account Reactivated',
      'role_change': 'Role Changed',
      'permission_change': 'Permissions Updated',
      'login_success': 'Logged In',
      'login_failed': 'Failed Login Attempt',
      'logout': 'Logged Out',
      'workspace_member_added': 'Added to Workspace',
      'workspace_member_removed': 'Removed from Workspace'
    };
    return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  const getEventIcon = (type, severity) => {
    if (type.includes('deactivat')) return '🔴';
    if (type.includes('reactivat')) return '🟢';
    if (type.includes('login_success')) return '✅';
    if (type.includes('login_failed')) return '⚠️';
    if (type.includes('role') || type.includes('permission')) return '🔧';
    return '📝';
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Activity Log
          </DialogTitle>
          <DialogDescription>
            {member?.user?.first_name} {member?.user?.last_name} ({member?.user?.email})
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading activity log...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No activity recorded yet</div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div key={log.id} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="text-lg">{getEventIcon(log.event_type, log.severity)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {formatEventType(log.event_type)}
                    </p>
                    {log.actor_email && log.actor_email !== member?.user?.email && (
                      <p className="text-xs text-gray-500">by {log.actor_email}</p>
                    )}
                    {log.new_value?.reason && (
                      <p className="text-xs text-gray-600 mt-1">
                        Reason: {log.new_value.reason}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main MembersTab component
const MembersTab = ({ members: initialMembers, roles, workspace, onRefresh, showInviteDialog, setShowInviteDialog }) => {
  const [members, setMembers] = useState(initialMembers);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, pending: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  
  // Dialog states
  const [selectedMember, setSelectedMember] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const canManageMembers = workspace?.role?.permissions?.members?.invite || 
                          workspace?.role?.permissions?.members?.changeRoles;

  // Load stats and members with filters
  useEffect(() => {
    loadStats();
    loadMembers();
  }, [showInactive, workspace]);
  
  const loadStats = async () => {
    if (!workspace?.workspace?.id) return;
    try {
      const response = await apiClient.get(`/api/v1/workspaces/${workspace.workspace.id}/members/stats`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };
  
  const loadMembers = async () => {
    if (!workspace?.workspace?.id) return;
    try {
      const response = await apiClient.get(
        `/api/v1/workspaces/${workspace.workspace.id}/members`,
        { params: { include_inactive: showInactive } }
      );
      if (response.data.success) {
        setMembers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  // Filter members based on search and status
  const filteredMembers = members.filter(member => {
    const matchesSearch = searchTerm === '' || 
      `${member.user.first_name} ${member.user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleDeactivate = async (reason) => {
    if (!selectedMember) return;
    
    try {
      setActionLoading(true);
      await apiClient.put(
        `/api/v1/workspaces/${workspace.workspace.id}/members/${selectedMember.id}/status`,
        null,
        { params: { action: 'deactivate', reason: reason || undefined } }
      );
      
      setShowDeactivateDialog(false);
      setSelectedMember(null);
      loadMembers();
      loadStats();
      onRefresh?.();
    } catch (error) {
      console.error('Error deactivating member:', error);
      alert(error.response?.data?.detail || 'Failed to deactivate member');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedMember) return;
    
    try {
      setActionLoading(true);
      await apiClient.put(
        `/api/v1/workspaces/${workspace.workspace.id}/members/${selectedMember.id}/status`,
        null,
        { params: { action: 'reactivate' } }
      );
      
      setShowReactivateDialog(false);
      setSelectedMember(null);
      loadMembers();
      loadStats();
      onRefresh?.();
    } catch (error) {
      console.error('Error reactivating member:', error);
      alert(error.response?.data?.detail || 'Failed to reactivate member');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setShowEditDialog(true);
  };

  const handleViewActivityLog = (member) => {
    setSelectedMember(member);
    setShowActivityLog(true);
  };

  // Get current user ID from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div data-testid="members-tab">
      {/* Summary Cards */}
      <MemberStatsCards stats={stats} />
      
      {/* Header with actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold">Team Members</h2>
          <p className="text-sm text-gray-600 mt-1">
            {filteredMembers.length} {filteredMembers.length === 1 ? 'member' : 'members'} shown
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
              data-testid="member-search-input"
            />
          </div>
          
          {/* Status filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Show inactive toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300"
              data-testid="show-inactive-checkbox"
            />
            Show inactive
          </label>
          
          {/* Invite button */}
          {canManageMembers && (
            <Button
              onClick={() => setShowInviteDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="invite-member-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          )}
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200" data-testid="members-table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Member
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Active
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                OEM/Region Access
              </th>
              {canManageMembers && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredMembers.map((member) => (
              <tr 
                key={member.id} 
                className={`hover:bg-gray-50 ${member.status === 'inactive' ? 'bg-gray-50 opacity-75' : ''}`}
                data-testid={`member-row-${member.user.email}`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                      member.status === 'inactive' ? 'bg-gray-200' : 'bg-blue-100'
                    }`}>
                      <span className={`font-medium text-sm ${
                        member.status === 'inactive' ? 'text-gray-500' : 'text-blue-600'
                      }`}>
                        {member.user.first_name?.[0]}{member.user.last_name?.[0]}
                      </span>
                    </div>
                    <div className="ml-4">
                      <div className={`text-sm font-medium ${
                        member.status === 'inactive' ? 'text-gray-500' : 'text-gray-900'
                      }`}>
                        {member.user.first_name} {member.user.last_name}
                        {member.user.id === currentUser?.id && (
                          <span className="ml-2 text-xs text-gray-400">(you)</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">{member.user.email}</div>
                      {member.user.title && (
                        <div className="text-xs text-gray-400">{member.user.title}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    member.role.name === 'Admin' ? 'bg-purple-100 text-purple-800' :
                    member.role.name === 'Manager' ? 'bg-blue-100 text-blue-800' :
                    member.role.name === 'Member' ? 'bg-green-100 text-green-800' :
                    member.role.name === 'Analyst' ? 'bg-cyan-100 text-cyan-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {member.role.name}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge status={member.status} />
                  {member.deactivated_at && (
                    <div className="text-xs text-gray-400 mt-1">
                      {formatRelativeTime(member.deactivated_at)}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatRelativeTime(member.last_active_at)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {member.assigned_oems && member.assigned_oems.length > 0 ? (
                      member.assigned_oems.slice(0, 2).map((oem) => (
                        <span key={oem} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                          {oem}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400">All OEMs</span>
                    )}
                    {member.assigned_oems && member.assigned_oems.length > 2 && (
                      <span className="text-xs text-gray-400">+{member.assigned_oems.length - 2}</span>
                    )}
                  </div>
                </td>
                {canManageMembers && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          data-testid={`member-actions-${member.user.email}`}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditMember(member)}>
                          Edit Role & Access
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleViewActivityLog(member)}>
                          <History className="w-4 h-4 mr-2" />
                          View Activity Log
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {member.status === 'active' ? (
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-600"
                            onClick={() => {
                              setSelectedMember(member);
                              setShowDeactivateDialog(true);
                            }}
                            disabled={member.user.id === currentUser?.id}
                          >
                            <UserX className="w-4 h-4 mr-2" />
                            Deactivate
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            className="text-green-600 focus:text-green-600"
                            onClick={() => {
                              setSelectedMember(member);
                              setShowReactivateDialog(true);
                            }}
                          >
                            <UserCheck className="w-4 h-4 mr-2" />
                            Reactivate
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                )}
              </tr>
            ))}
            {filteredMembers.length === 0 && (
              <tr>
                <td colSpan={canManageMembers ? 6 : 5} className="px-6 py-12 text-center text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No members found</p>
                  {searchTerm && <p className="text-sm mt-1">Try adjusting your search</p>}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Member Dialog - reuse existing */}
      {showEditDialog && selectedMember && (
        <EditMemberDialog
          member={selectedMember}
          roles={roles}
          workspace={workspace}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedMember(null);
          }}
          onSuccess={() => {
            setShowEditDialog(false);
            setSelectedMember(null);
            loadMembers();
            onRefresh?.();
          }}
        />
      )}

      {/* Deactivate Dialog */}
      <DeactivateDialog
        open={showDeactivateDialog}
        onClose={() => {
          setShowDeactivateDialog(false);
          setSelectedMember(null);
        }}
        member={selectedMember}
        onConfirm={handleDeactivate}
        loading={actionLoading}
      />

      {/* Reactivate Dialog */}
      <ReactivateDialog
        open={showReactivateDialog}
        onClose={() => {
          setShowReactivateDialog(false);
          setSelectedMember(null);
        }}
        member={selectedMember}
        onConfirm={handleReactivate}
        loading={actionLoading}
      />

      {/* Activity Log Dialog */}
      <ActivityLogDialog
        open={showActivityLog}
        onClose={() => {
          setShowActivityLog(false);
          setSelectedMember(null);
        }}
        member={selectedMember}
        workspace={workspace}
      />

      {/* Invite Member Dialog - passed from parent */}
      {showInviteDialog && (
        <InviteMemberDialog
          roles={roles}
          workspace={workspace}
          onClose={() => setShowInviteDialog(false)}
          onSuccess={() => {
            setShowInviteDialog(false);
            loadMembers();
            loadStats();
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
};

// Edit Member Dialog (extracted for reuse)
const EditMemberDialog = ({ member, roles, workspace, onClose, onSuccess }) => {
  const [selectedRoleId, setSelectedRoleId] = useState(member.role.id);
  const [assignedOems, setAssignedOems] = useState(member.assigned_oems?.join(', ') || '');
  const [assignedRegions, setAssignedRegions] = useState(member.assigned_regions?.join(', ') || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const oemArray = assignedOems.split(',').map(s => s.trim()).filter(Boolean);
      const regionArray = assignedRegions.split(',').map(s => s.trim()).filter(Boolean);
      
      await apiClient.put(
        `/api/v1/workspaces/${workspace.workspace.id}/members/${member.id}`,
        null,
        {
          params: {
            role_id: selectedRoleId,
            assigned_oems: oemArray.length > 0 ? oemArray : null,
            assigned_regions: regionArray.length > 0 ? regionArray : null
          }
        }
      );
      
      onSuccess();
    } catch (error) {
      console.error('Error updating member:', error);
      alert('Failed to update member: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Member</DialogTitle>
          <DialogDescription>
            {member.user.first_name} {member.user.last_name} ({member.user.email})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assigned OEMs (comma-separated)
            </label>
            <Input
              value={assignedOems}
              onChange={(e) => setAssignedOems(e.target.value)}
              placeholder="e.g., GM, Ford, Toyota"
            />
            <p className="text-xs text-gray-500 mt-1">Leave empty for all OEMs</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assigned Regions (comma-separated)
            </label>
            <Input
              value={assignedRegions}
              onChange={(e) => setAssignedRegions(e.target.value)}
              placeholder="e.g., North America, Europe"
            />
            <p className="text-xs text-gray-500 mt-1">Leave empty for all regions</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Invite Member Dialog (extracted for reuse)
const InviteMemberDialog = ({ roles, workspace, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState(roles[0]?.id || '');
  const [assignedOems, setAssignedOems] = useState('');
  const [assignedRegions, setAssignedRegions] = useState('');
  const [sending, setSending] = useState(false);

  const handleInvite = async () => {
    if (!email) {
      alert('Please enter an email address');
      return;
    }

    try {
      setSending(true);
      
      const oemArray = assignedOems.split(',').map(s => s.trim()).filter(Boolean);
      const regionArray = assignedRegions.split(',').map(s => s.trim()).filter(Boolean);
      
      await apiClient.post(
        `/api/v1/workspaces/${workspace.workspace.id}/invitations`,
        null,
        {
          params: {
            email,
            role_id: selectedRoleId,
            assigned_oems: oemArray.length > 0 ? oemArray : null,
            assigned_regions: regionArray.length > 0 ? regionArray : null
          }
        }
      );
      
      alert('Invitation sent successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error inviting member:', error);
      alert('Failed to invite member: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
            <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assigned OEMs (optional)
            </label>
            <Input
              value={assignedOems}
              onChange={(e) => setAssignedOems(e.target.value)}
              placeholder="e.g., GM, Ford, Toyota"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assigned Regions (optional)
            </label>
            <Input
              value={assignedRegions}
              onChange={(e) => setAssignedRegions(e.target.value)}
              placeholder="e.g., North America, Europe"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleInvite} disabled={sending}>
            {sending ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MembersTab;
